import JSCookie from 'js-cookie';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { debounce, useBoolean } from '@sravni/react-utils';

import { SravniQuizModelsFiltersOption } from '../../__generated__/quiz/Api';
import { useFingerprint } from '../../hooks/users';
import { NormalizedVoteWithPercentOptions, OptionsWithPercents } from '../../server/services/quiz';
import { addVote } from '../../services/quiz';

import { Survey } from './Survey';

const generateCookieNameForCompletedSurvey = (voteId) => `survey-${voteId}`;

export const SurveyWidget = ({ surveyInfo }: { surveyInfo: NormalizedVoteWithPercentOptions }) => {
  const { theme = 'purple', name, text, options, isMultipleAnswers, isClosed, id: voteId, isUserVoted } = surveyInfo;

  const [optionsWithPercents, setOptionsWithPercents] = useState<OptionsWithPercents>(options);
  const fingerprint = useFingerprint();
  const [isClosedFromCookie, setIsClosedFromCookie] = useState<boolean>(isClosed);
  const [wasVoteSent, setWasVoteSent] = useBoolean(false);
  const componentRef = useRef();

  const router = useRouter();
  const isPreview = router.query.preview === '';

  const isSurveyCompleted = isClosed || wasVoteSent || isClosedFromCookie || isUserVoted || isPreview;

  const onSubmit = async (optionIds: SravniQuizModelsFiltersOption[]) => {
    try {
      const results = await addVote({
        voteId,
        fingerPrint: fingerprint,
        options: optionIds,
      });

      setOptionsWithPercents(results.options);
      setWasVoteSent.on();
      JSCookie.set(generateCookieNameForCompletedSurvey(voteId), 'completed');
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const isClosedFromCookie = JSCookie.get(generateCookieNameForCompletedSurvey(voteId)) === 'completed' ?? false;
    setIsClosedFromCookie(isClosedFromCookie);
  }, [voteId]);

  const sendHeight = (payload: { surveyId: string; height: number }) => {
    window.parent.postMessage(
      {
        source: 'news-survey',
        payload,
      },
      '*',
    );
  };

  useEffect(() => {
    const block = componentRef.current;

    let resizeObserver = new ResizeObserver(
      debounce((entries) => {
        entries.forEach((entry) => {
          const { height } = entry.contentRect;

          if (height) {
            sendHeight({ surveyId: voteId, height });
          }
        });
      }, 300),
    );

    resizeObserver.observe(block);

    return () => {
      resizeObserver?.unobserve(block);
    };
  }, [voteId]);

  return (
    <div ref={componentRef}>
      <Survey
        theme={theme}
        isSurveyCompleted={isSurveyCompleted}
        title={name}
        comment={text}
        onSubmit={onSubmit}
        options={optionsWithPercents}
        isMultipleAnswers={isMultipleAnswers}
      />
    </div>
  );
};
